import React, { Component, ReactNode } from "react";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import TreeView from "@material-ui/lab/TreeView";
import { Box } from "@material-ui/core";
import DeviceGroup from "data/device/DeviceGroup";
import Device from "data/device/Device";
import Localization from "data/localization-sensoan/Localization";
import { Maybe } from "types/aliases";
import ScrollDrag from "components/ui/scroll-drag";
import Loader from "components/ui/loader";
import SSvgIcon, { SSvgIconColorProps } from "components/styled-components/SSvgIcon";
import DeviceTreeLevel from "./components/device-tree-level";
import SearchFilter from "./helpers/search-filter";
import SelectionRecorder from "./helpers/selection-recorder";


interface Props {
  groups: DeviceGroup[];
  selectedDevice?: string;
  selectedGroup?: string;
  editMode?: boolean;
  searchFilter?: SearchFilter;
  keepSingleRootExpanded?: boolean;
  "data-testid"?: string;
  onDeviceSelect?: (device?: Device) => void;
  onGroupSelect?: (group?: DeviceGroup) => void;
  deviceFilter?: (device: Device) => void;
}

interface State {
  expandedItemIds: string[];
  selectedItemId?: string;
}

class DeviceTree extends Component<Props, State> {
  private static storedExpandedItems: string[] = [];
  private selectionRecorder = new SelectionRecorder<Device>(device => device?.getId() === this.props.selectedDevice);

  public constructor(props: Props) {
    super(props);
    const expandedItemIds = this.getExpandedItems(DeviceTree.storedExpandedItems, props);
    DeviceTree.storedExpandedItems = expandedItemIds;
    this.state = { expandedItemIds };
  }

  private get keepRootsExpanded(): boolean {
    return this.props.groups.length === 1 && !!this.props.keepSingleRootExpanded;
  }

  private getExpandedItems(newExpandedItems: string[], props: Props = this.props): string[] {
    return this.keepRootsExpanded
      ? [...new Set([...newExpandedItems, ...props.groups.map(group => group.getId())])]
      : newExpandedItems;
  }

  private handleNodeToggle = (_event: unknown, expandedNodeIds: string[]): void => {
    const expandedItemIds = this.getExpandedItems(expandedNodeIds);
    DeviceTree.storedExpandedItems = expandedItemIds;
    this.setState({ expandedItemIds });
  };

  private renderLevels(): Maybe<ReactNode> {
    if (!this.props.groups.length) {
      return (<Box m={2}>{Localization.getInstance().getDisplayText("DeviceDrawer", "noDevicesOrGroupsFound")}</Box>);
    }
    const onDeviceSelect = this.selectionRecorder.getHandler(this.props.onDeviceSelect, this.props.selectedDevice);
    return this.props.groups.map((group: DeviceGroup, index: number) => (
      <DeviceTreeLevel
        key={group.getId()}
        group={group}
        editMode={this.props.editMode}
        searchFilter={this.props.searchFilter}
        selectedDevice={this.props.selectedDevice}
        selectedGroup={this.props.selectedGroup}
        onDeviceSelect={onDeviceSelect}
        onGroupSelect={this.props.onGroupSelect}
        hideFolderControlIcon={this.keepRootsExpanded}
        data-testid={this.props["data-testid"] ? `${this.props["data-testid"]}-group-${index}` : undefined}
        deviceFilter={this.props.deviceFilter}
      />
    ));
  }

  private renderTreeView(): JSX.Element {
    return (
      <TreeView
        expanded={this.state.expandedItemIds}
        defaultExpandIcon={<SSvgIcon color={SSvgIconColorProps.secondary} iconComponent={ChevronRightIcon} />}
        defaultCollapseIcon={<SSvgIcon color={SSvgIconColorProps.secondary} iconComponent={ExpandMoreIcon} />}
        onNodeToggle={this.handleNodeToggle}
        data-testid={this.props["data-testid"]}
      >
        {this.renderLevels()}
      </TreeView>
    );
  }

  public render(): JSX.Element {
    if (!this.props.groups) {
      return <Loader/>;
    }

    if (this.props.editMode) {
      return (
        <ScrollDrag rootClass={"tree-list"}>
          {this.renderTreeView()}
        </ScrollDrag>
      );
    } else {
      return this.renderTreeView();
    }
  }
}

export default DeviceTree;
